import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faStore,
} from "@fortawesome/free-solid-svg-icons";

import Style from "./Header.module.css";
import useScreenSize from "../../hooks/useScreenSize";
import { slide as Menu } from "react-burger-menu";

import "./HamburgerMenu.css";
import PrimaryButton from "../UI/Buttons/PrimaryButton";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../../i18n/I18nContext";

const Header = () => {
  const { isMediumSize, isSmallSize } = useScreenSize();
  const location = useLocation();
  const { copy } = useI18n();

  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const productsButtonRef = useRef(null);

  const showHamburgerMenu = isMediumSize || isSmallSize;
  const isProductsRoute = location.pathname === "/wecp";

  useEffect(() => {
    setProductsOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showHamburgerMenu) {
      return;
    }

    document
      .getElementById("react-burger-menu-btn")
      ?.setAttribute("aria-label", copy.common.openMenu);
    document
      .getElementById("react-burger-cross-btn")
      ?.setAttribute("aria-label", copy.common.closeMenu);
  }, [copy, showHamburgerMenu, open]);

  const closeMobileMenu = () => {
    setOpen(false);
    setMobileProductsOpen(false);
  };

  const handleProductsKeyDown = (event) => {
    if (event.key !== "Escape") {
      return;
    }

    setProductsOpen(false);
    productsButtonRef.current?.focus();
  };

  const desktopNavigation = (
    <nav className={Style.nav}>
      <NavLink exact to="/" activeClassName={Style["active"]}>
        {copy.navigation.home}
      </NavLink>
      <NavLink exact to="/services" activeClassName={Style["active"]}>
        {copy.navigation.services}
      </NavLink>
      <NavLink exact to="/technologies" activeClassName={Style["active"]}>
        {copy.navigation.technologies}
      </NavLink>
      <NavLink exact to="/about-us" activeClassName={Style["active"]}>
        {copy.navigation.aboutUs}
      </NavLink>
      <div
        className={Style["product-menu"]}
        onMouseEnter={() => setProductsOpen(true)}
        onMouseLeave={() => setProductsOpen(false)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setProductsOpen(false);
          }
        }}
        onKeyDown={handleProductsKeyDown}
      >
        <button
          ref={productsButtonRef}
          type="button"
          className={`${Style["product-trigger"]} ${
            isProductsRoute ? Style["product-trigger--active"] : ""
          }`}
          aria-haspopup="true"
          aria-expanded={productsOpen}
          onClick={() => setProductsOpen((current) => !current)}
        >
          {copy.navigation.products}
          <FontAwesomeIcon
            className={productsOpen ? Style["product-trigger__icon--open"] : ""}
            icon={faChevronDown}
            aria-hidden="true"
          />
        </button>

        {productsOpen && (
          <div className={Style["product-dropdown"]}>
            <NavLink
              exact
              to="/wecp"
              className={Style["product-dropdown__link"]}
              activeClassName={Style["product-dropdown__link--active"]}
              onClick={() => setProductsOpen(false)}
            >
              <span className={Style["product-dropdown__icon"]}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
              </span>
              <span className={Style["product-dropdown__copy"]}>
                <strong>WECP</strong>
                <span>{copy.navigation.wecpDescription}</span>
              </span>
            </NavLink>
          </div>
        )}
      </div>
      <LanguageSwitcher />
      <PrimaryButton
        onClick={() =>
          window.open("https://calendly.com/contact-warenex/30min")
        }
        padding="1rem 2rem"
        borderRadius="30px"
      >
        {copy.common.bookMeeting}
      </PrimaryButton>
    </nav>
  );

  const mobileViewNavigation = (
    <Menu
      right
      styles={{ position: "absolute", top: 0 }}
      isOpen={open}
      onOpen={() => setOpen(true)}
      onClose={closeMobileMenu}
    >
      <NavLink exact onClick={() => setOpen(false)} to="/">
        {copy.navigation.home}
      </NavLink>
      <NavLink onClick={() => setOpen(false)} to="/services">
        {copy.navigation.services}
      </NavLink>
      <NavLink onClick={() => setOpen(false)} to="/technologies">
        {copy.navigation.technologies}
      </NavLink>
      <NavLink onClick={() => setOpen(false)} to="/about-us">
        {copy.navigation.aboutUs}
      </NavLink>
      <button
        type="button"
        className="mobile-products-trigger"
        aria-expanded={mobileProductsOpen}
        onClick={() => setMobileProductsOpen((current) => !current)}
      >
        {copy.navigation.products}
        <FontAwesomeIcon
          className={mobileProductsOpen ? "mobile-products-icon-open" : ""}
          icon={faChevronDown}
          aria-hidden="true"
        />
      </button>
      {mobileProductsOpen && (
        <NavLink
          className="mobile-product-link"
          onClick={closeMobileMenu}
          to="/wecp"
        >
          <FontAwesomeIcon icon={faStore} aria-hidden="true" />
          <span>
            <strong>WECP</strong>
            <small>{copy.navigation.wecpDescription}</small>
          </span>
        </NavLink>
      )}
      <LanguageSwitcher />
      <hr></hr>
      <a
        target="_blank"
        rel="noreferrer"
        href="https://calendly.com/contact-warenex/30min"
      >
        {copy.common.bookMeeting}
      </a>
    </Menu>
  );
  return (
    <header className={Style.header}>
      <div className={Style["header__inner-container"]}>
        <Link to="/" className={Style["nav-logo"]}>
          <img src="nav-logo.png" alt={copy.common.navigationLogoAlt} />
        </Link>
        {showHamburgerMenu ? mobileViewNavigation : desktopNavigation}
      </div>
    </header>
  );
};

export default Header;
